import React, { useState, useEffect } from 'react';
import { FormControl, InputLabel, Select, MenuItem, CircularProgress } from '@mui/material';
import { organizationApi } from '../../../services/api';
import { useAlert } from '../../../contexts/AlertContext';
import { ORG_UNIT_TYPE_RETURN_TYPES } from '../../../constants/selectorReturnTypes';

// Legacy export for backward compatibility
export const RETURN_VALUE_TYPES = ORG_UNIT_TYPE_RETURN_TYPES;

const OrgUnitTypeSelector = ({ 
  value, 
  onChange, 
  label = "Organizational Unit Type",
  required = false,
  disabled = false,
  fullWidth = true,
  returnValue = ORG_UNIT_TYPE_RETURN_TYPES.ID, // ORG_UNIT_TYPE_RETURN_TYPES.ID or ORG_UNIT_TYPE_RETURN_TYPES.NAME
  showEmptyOption = true,
  emptyOptionLabel = "None",
  // Ids to leave OUT of the list. Added for ADR-097's policy page, where a
  // type that already has a rule must not be offered again — the unique
  // constraint then backs the UI up instead of surfacing as an error the user
  // has to read. Default empty: every existing caller is unaffected.
  excludeIds = [],
  ...props 
}) => {
  const [orgUnitTypes, setOrgUnitTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { showAlert } = useAlert();

  useEffect(() => {
    const fetchOrgUnitTypes = async () => {
      try {
        setIsLoading(true);
        const orgUnitTypesData = await organizationApi.getOrgUnitTypes();
        setOrgUnitTypes(orgUnitTypesData);
      } catch (error) {
        showAlert('Failed to load organizational unit types', 'error', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrgUnitTypes();
  }, [showAlert]);

  const handleChange = (event) => {
    if (onChange) {
      const selectedOrgUnitTypeId = event.target.value;

      if (returnValue === ORG_UNIT_TYPE_RETURN_TYPES.NAME) {
        // Find the organizational unit type object and return its name
        const selectedOrgUnitType = orgUnitTypes.find(type => type.id === selectedOrgUnitTypeId);
        onChange(selectedOrgUnitType?.name || '');
      } else if (returnValue === ORG_UNIT_TYPE_RETURN_TYPES.CODE) {
        // Return the environment-stable code (ADR-093: filters with fixed defaults)
        const selectedOrgUnitType = orgUnitTypes.find(type => type.id === selectedOrgUnitTypeId);
        onChange(selectedOrgUnitType?.code || '');
      } else {
        // Return the ID (default behavior)
        onChange(selectedOrgUnitTypeId);
      }
    }
  };

  // Find the selected organizational unit type object from the ID, name, or code
  const selectedOrgUnitType = orgUnitTypes.find(type => {
    if (returnValue === ORG_UNIT_TYPE_RETURN_TYPES.NAME) {
      return type.name === value;
    } else if (returnValue === ORG_UNIT_TYPE_RETURN_TYPES.CODE) {
      return type.code === value;
    } else {
      return type.id === value;
    }
  }) || null;

  return (
    <FormControl fullWidth={fullWidth} required={required} disabled={disabled || isLoading} {...props}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={selectedOrgUnitType?.id || ''}
        onChange={handleChange}
        label={label}
        disabled={disabled || isLoading}
        endAdornment={
          isLoading ? <CircularProgress color="inherit" size={20} /> : null
        }
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
              zIndex: 1350 // Ensure dropdown appears above table
            }
          }
        }}
      >
        {/* Empty option for non-required fields */}
        {!required && showEmptyOption && (
          <MenuItem value="">
            <em>{emptyOptionLabel}</em>
          </MenuItem>
        )}
        
        {orgUnitTypes
          // Never hide what is currently selected: a control that cannot
          // display its own value looks broken. Compared against the RESOLVED
          // selection rather than `value`, because `value` is a code or a name
          // in those return modes, and would never equal an id.
          .filter(type => !(excludeIds || []).includes(type.id)
            || type.id === selectedOrgUnitType?.id)
          .map(type => (
          <MenuItem key={type.id} value={type.id}>
            {type.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default OrgUnitTypeSelector;